import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { RootStore } from './index';
import { BaseStore } from './base.store';
import moment from 'moment';
import notificationService from '../../services/notification.service';
import {
    convertNotification,
    NotificationDownloadStatus,
    NotificationDto,
    NotificationResponseStatus
} from '../../dtos/notification.dto';
import logsService from '../../services/logs.service';
import { delayPromise } from 'src/utils/delay';
import { LOADER_MINIMUM_MS, NOTIFICATION_TEXT } from 'src/utils/constants';
import { triggerFileDownload } from '../../utils/activity-util';
import userInfoService from '../../services/user-info.service';
import { WHATS_NEW_ENTRIES } from '@src/components/features/notification/whats-new/data/whats-new-data';
import { hasUnreadEntries } from '@src/components/features/notification/whats-new/data/whats-new-utils';

export class NotificationStore extends BaseStore {
    public lastSeenDate = moment().utc();
    public notifications: NotificationDto[] = [];
    public isNotificationTooltipShow: boolean = false;
    public notificationPopoverText: string = NOTIFICATION_TEXT;
    public isLoading: boolean = false;
    public notificationVisible: boolean = false;
    public notificationDownloadStatus: { [key: string]: NotificationDownloadStatus } = {};
    public whatsNewLastSeenDate: moment.Moment | null = null;
    public isWhatsNewLoading: boolean = false;

    constructor(rootStore: RootStore) {
        super(rootStore);

        makeObservable(this, {
            lastSeenDate: observable,
            notifications: observable,
            isNotificationTooltipShow: observable,
            newNotificationsCount: computed,
            setIsNotificationTooltipShow: action,
            isLoading: observable,
            notificationDownloadStatus: observable,
            setNotificationDownloadStatus: action,
            notificationPopoverText: observable,
            setNotificationPopoverText: action,
            notificationVisible: observable,
            setNotificationVisible: action,
            whatsNewLastSeenDate: observable,
            isWhatsNewLoading: observable,
            hasWhatsNewUnread: computed,
            loadWhatsNewLastSeen: action,
            updateWhatsNewLastSeenDate: action,
        });
    }

    public isNewNotification(notification: NotificationDto) {
        return notification.notificationTimestamp.isAfter(this.rootStore.userPreferencesStore.lastSeenNotification);
    }

    get newNotificationsCount() {
        return this.notifications.filter(n => this.isNewNotification(n)).length;
    }


    setIsNotificationTooltipShow(shouldShow: boolean) {
        this.isNotificationTooltipShow = shouldShow;
    }

    setNotificationVisible(visible: boolean) {
        this.notificationVisible = visible;
    }

    get hasWhatsNewUnread(): boolean {
        return hasUnreadEntries(WHATS_NEW_ENTRIES, this.whatsNewLastSeenDate);
    }

    public async loadWhatsNewLastSeen(): Promise<void> {
        if (this.isWhatsNewLoading) return;
        runInAction(() => { this.isWhatsNewLoading = true; });
        try {
            const response = await userInfoService.getWhatsNewLastSeen();
            runInAction(() => {
                this.whatsNewLastSeenDate = response.lastSeenDate
                    ? moment.utc(response.lastSeenDate)
                    : null;
            });
        } catch (err) {
            console.error('Failed to load What\'s New last-seen date', err);
            runInAction(() => { this.whatsNewLastSeenDate = null; });
        } finally {
            runInAction(() => { this.isWhatsNewLoading = false; });
        }
    }

    public async updateWhatsNewLastSeenDate(): Promise<void> {
        const nowUtc = moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS');
        // Optimistic update — dot disappears immediately without waiting for network
        runInAction(() => { this.whatsNewLastSeenDate = moment.utc(nowUtc); });
        try {
            await userInfoService.updateWhatsNewLastSeen({ lastSeenDate: nowUtc });
        } catch (err) {
            console.error('Failed to update What\'s New last-seen date', err);
            // Do NOT roll back the optimistic update — dot will reappear on next panel open
        }
    }

    public async loadNotifications(fetchAllServices = false) {
        const { customerId, serviceId } = this.rootStore.customerStore;
        this.setLoading(true);
        try {
            const promise = notificationService.getNotificationList(customerId, fetchAllServices ? undefined : serviceId);
            const res = await Promise.all([promise, delayPromise(LOADER_MINIMUM_MS)]);

            runInAction(() => {
                this.notifications = [...res[0].userNotifications
                    .map(convertNotification)
                    .sort(this.notificationComparator)];

                this.notifications.forEach(notification => {
                    this.initNotificationDownloadStatus(notification.id);
                });
            });
        }
        finally {
            this.setLoading(false);
        }
    }

    private initNotificationDownloadStatus(notificationId: string) {
        if (!this.notificationDownloadStatus[notificationId]) {
            this.setNotificationDownloadStatus(notificationId, NotificationDownloadStatus.NOT_DOWNLOADED);
        }
    }

    public setNotificationDownloadStatus(notificationId: string, status: NotificationDownloadStatus) {
        this.notificationDownloadStatus[notificationId] = status;
    }

    public markNotificationAsDownloadFailure(notificationId: string) {
        this.setNotificationDownloadStatus(notificationId, NotificationDownloadStatus.NOT_DOWNLOADED);
    }

    public async downloadLogFile(notificationId: string, filename: string): Promise<any> {
        const { customerId, serviceId } = this.rootStore.customerStore;
        this.setNotificationDownloadStatus(notificationId, NotificationDownloadStatus.DOWNLOADING);
        const fileBlob = await logsService.downloadLogsFile(customerId, serviceId, filename);
        triggerFileDownload(fileBlob, filename);
        this.setNotificationDownloadStatus(notificationId, NotificationDownloadStatus.DOWNLOADED);

    }

    public setNotificationPopoverText(text: string) {
        this.notificationPopoverText = text;
    }

    private notificationComparator(a: NotificationDto, b: NotificationDto): number {
        const aTimestamp = moment(a.notificationTimestamp);
        const bTimestamp = moment(b.notificationTimestamp);

        const { NOTIFICATION_PENDING } = NotificationResponseStatus;

        if (a.status === NOTIFICATION_PENDING && b.status !== NOTIFICATION_PENDING) {
            return -1;
        } else if (a.status !== NOTIFICATION_PENDING && b.status === NOTIFICATION_PENDING) {
            return 1;
        } else {
            return aTimestamp.isAfter(bTimestamp) ? -1 : 1;
        }
    }

    private setLoading(isLoading: boolean) {
        runInAction(() => {
            this.isLoading = isLoading;
        });
    }
}